import router from '@/router'
import { JWTService } from '@/shared/services/jwt'

// Lazy require para quebrar circular dep: api.js → @/store → ./module → company/actions.js → ...
// O interceptor só roda em runtime (após boot), então o store já está totalmente inicializado.
const getStore = () => require('@/store').default

// O corpo que o `AuthorizationMiddleware` devolve quando NÃO há sessão. `JsonReturn` serializa
// `eCodeReturn.FORBIDDEN`, que vale **-403** (Libraries/Helpers/General/eCodeReturn.cs:32) — é
// negativo mesmo, e é isso que distingue o envelope do middleware de um 403 de controller.
const CODIGO_SEM_SESSAO = -403

/**
 * 403 no zCloud tem DOIS emissores e eles querem coisas opostas:
 *
 * 1. `AuthorizationMiddleware` (Libraries/Proxy/Middleware) — sem cookie `zcloud_token`, ou token
 *    que não é um JWT de 3 partes. Isso é SEM SESSÃO e tem que mandar pro SignIn. Note que ele
 *    responde 403, não 401: o 401 dele é só para token expirado e assinatura inválida.
 * 2. Os controllers — gate de permissão (`MotivoNegarComprovantes*`, `BaseController` do PCI,
 *    `QMAConsultasController`). Isso é SEM PERMISSÃO, com o usuário logado, e deslogar é errado.
 *
 * O caso concreto que fez esta função existir: a conta de consulta do Diversos abria
 * `/despesa/diversos/colaborador/detalhes/funcionario` (a rota aceita `ACC_ZPAG_DIVERSOS_CONSULTA`)
 * e, ao clicar "Gerar relatório", tomava o 403 do gate e era jogada pro login. O `catch` do
 * `gerarRelatorioSelecionados` até monta o toast com o motivo, mas o redirect global chega antes.
 *
 * O que separa os dois é o CORPO: o middleware devolve o envelope `JsonReturn`, e hoje nenhum
 * controller devolve esse envelope num HTTP 403 — eles devolvem o motivo como string, como
 * `{ message }` anônimo, ou corpo vazio. O critério é a FORMA do envelope, não a identidade do
 * emissor: `GedPastaController` monta um `JsonReturn` com `Code = FORBIDDEN`, e escapa só
 * porque responde HTTP 200. Se algum dia um controller devolver o envelope com 403, ele vai
 * deslogar — é a troca aceita, e o caso está coberto no spec pra que a decisão seja visível.
 *
 * Por que a função é async: com `responseType: 'blob'` (13 usos) e `'arraybuffer'` (2, no GED)
 * o corpo do erro não chega como string. `Blob` é relido sem consumir, então o `catch` do
 * chamador continua enxergando o mesmo corpo.
 */
// `TextDecoder` não existe no IE11, que o `browserslist` deste repo (`> 1%`, `last 2 versions`)
// ainda pode alcançar. O fallback é latin1 byte a byte: para o que esta função DECIDE — achar
// `Code`/`code` no envelope — isso basta, porque o JSON continua parseável e só acento vira lixo,
// e acento não entra na decisão.
const decodifica = (buf) => {
  const bytes = new Uint8Array(buf instanceof ArrayBuffer ? buf : buf.buffer)
  if (typeof TextDecoder === 'function') return new TextDecoder('utf-8').decode(bytes)
  let texto = ''
  for (let i = 0; i < bytes.length; i++) texto += String.fromCharCode(bytes[i])
  return texto
}

const leCorpo = async (data) => {
  let bruto = data

  // `responseType: 'arraybuffer'` (o download do GED) entrega o corpo do erro como ArrayBuffer,
  // que NÃO tem `.text()`. Sem decodificar, o envelope do middleware não é reconhecido e quem
  // está sem sessão fica preso na tela em vez de ir pro SignIn — regressão achada pelo crítico
  // adversarial, com o caminho real em `documentsfolder.js` → `ArchiveDocumentsManagement.vue`.
  if (bruto instanceof ArrayBuffer || ArrayBuffer.isView(bruto)) {
    try {
      bruto = decodifica(bruto)
    } catch (e) {
      return null // buffer ilegível: corpo desconhecido
    }
  }

  if (bruto && typeof bruto.text === 'function') {
    try {
      bruto = await bruto.text()
    } catch (e) {
      return null // blob ilegível: trata como corpo desconhecido
    }
  }

  if (typeof bruto === 'string') {
    try {
      // string JSON pura (`"Sua conta nao tem permissao..."`) volta como string, não como objeto —
      // e é exatamente por isso que ela não passa no teste do `code`.
      return JSON.parse(bruto)
    } catch (e) {
      return null
    }
  }

  return bruto
}

// O envelope chega em DUAS grafias e as duas sao o mesmo `JsonReturn`. O middleware serializa com
// `JsonConvert.SerializeObject` cru, que respeita o nome declarado da propriedade, entao sai
// `Code`; o mesmo tipo devolvido por um controller passa pelo resolver camelCase do MVC e sai
// `code`. Medido em api.prod e api-aceite em 22/09/2026, sem token:
// `{"Code":-403,"Message":"Access denied","Data":null,"ResultException":null}`.
// Testar so uma das grafias deixa o usuario SEM sessao preso na tela, sem ir pro SignIn.
const ehEnvelopeSemSessao = (corpo) => {
  if (!corpo || typeof corpo !== 'object') return false
  const codigo = corpo.Code !== undefined ? corpo.Code : corpo.code
  return codigo === CODIGO_SEM_SESSAO
}

/**
 * Interceptor de erro compartilhado pelo `api.js` e pelo `apiupload.js`. Mora num módulo só porque
 * a regra é a mesma nos dois clients e duas cópias divergem na primeira vez que o critério muda.
 */
const redirecionaSemSessao = async (error) => {
  const status = error && error.response && String(error.response.status)

  // `/4\d[13]/` era SUBSTRING sem âncora: casava 411, 413, 421, 431, 451... e qualquer 4xx com
  // 1 ou 3 no fim. Achado da crítica adversarial do ZC-42, com efeito concreto: o 413 do teto do
  // relatório deslogava em vez de mostrar "marque menos lotes".
  if (status !== '401' && status !== '403') return Promise.reject(error)

  // 403 de permissão não é sessão inválida: rejeita e deixa o chamador mostrar o motivo.
  if (status === '403' && !ehEnvelopeSemSessao(await leCorpo(error.response.data))) {
    return Promise.reject(error)
  }

  // /authorize/me é probe de sessão (main.js, App.vue.mounted, authsso.vue, etc.).
  // 401 nele é o estado esperado para usuário ainda não autenticado.
  // /authorize/security-token (SSO) é tratado pelo authsso.vue — não pode ser interceptado globalmente.
  const requestUrl = (error.config && error.config.url) || ''
  const isAuthProbe = requestUrl.includes('/authorize/me') || requestUrl.includes('/authorize/security-token')

  // Se a rota corrente é AuthSSO, a autenticação ainda está em curso — não redirecionar globalmente.
  const currentRouteName = router.history && router.history.current && router.history.current.name
  const isOnSSORoute = currentRouteName === 'AuthSSO'

  if (isAuthProbe || isOnSSORoute) {
    // Apenas reflete no store que não há sessão ativa; deixa o chamador (ou o authsso.vue) decidir.
    getStore().commit('auth/setUserIsLoggedIn', false)
    getStore().commit('auth/setJWT', null)
    return Promise.reject(error)
  }

  if (process.env.NODE_ENV === 'development') {
    JWTService.DestroyToken() // limpa localStorage em dev
  }
  getStore().commit('auth/setUserIsLoggedIn', false)
  getStore().commit('auth/setJWT', null)
  if (currentRouteName !== 'SignIn') {
    router.replace({ name: 'SignIn' })
  }

  return Promise.reject(error)
}

export default redirecionaSemSessao



// WEBPACK FOOTER //
// ./src/shared/http/clients/redirecionaSemSessao.js